import { companyPeopleHeroEditorDefinitions } from './companyPeopleHeroEditorDefinitions.js';
import { companyJourneyEditorDefinitions } from './companyJourneyEditorDefinitions.js';
import { applicationPagePresentation } from '../../shared/content/applicationPageContent.js';
import { editorPages } from '../../shared/editor/landingEditorNavigation.js';
import { isIndustryPageKey } from '../../shared/content/industryPages.js';
import { industryGalleryEditorDefinitions, industryNavigationEditorDefinitions, industryStaticNavigationEditorDefinitions } from './industryPageEditorDefinitions.js';
import { mobileNavigationEditorDefinitions } from './mobileNavigationEditorDefinitions.js';
import { landingEditorSectionLabels } from './landingEditorSectionLabels.js';
import type { LandingContent } from '../../shared/content/landingContent.js';
import type { StaticPageContent, StaticPageKey } from '../../shared/content/staticPageContent.js';
import { appendMissingContentDefinitions } from './editorContentCoverage.js';
import { registerGlobusJobEditorDefinitions } from './globusJobEditorDefinitions.js';
import { landingHomeSectionKeys, type LandingHomeSectionKey } from '../../shared/editor/landingEditorTypes.js';

export type LandingEditorFieldKind =
  | 'text'
  | 'url'
  | 'image'
  | 'video'
  | 'track'
  | 'icon'
  | 'profession'
  | 'freshCounterSubtype'
  | 'section'
  | 'container';

export interface LandingEditorElementDefinition {
  id: string;
  label: string;
  kind: LandingEditorFieldKind;
  path?: string;
  source?: 'landing' | 'static';
  staticPageKey?: StaticPageKey;
  section?: LandingHomeSectionKey;
  parentId?: string;
  maskHostId?: string;
  repeatPath?: string;
  repeatIndex?: number;
  multiline?: boolean;
  typographyMode?: 'semantic' | 'visual' | 'none';
}

const typographyModeForText = (path: string): LandingEditorElementDefinition['typographyMode'] => {
  if (/(?:imageAlt|videoAlt|altText|\.alt)$/iu.test(path)) return 'none';
  if (/(?:^|\.)(?:title|intro|text|description|quote|answer|story|motivation)$/u.test(path)) return 'semantic';
  return 'visual';
};

const editorSectionPaletteKeys = landingHomeSectionKeys.filter(section => section !== 'consultationApplicant' && section !== 'consultationCompany');

export class LandingEditorElementRegistry {
  static definitions(content: LandingContent): LandingEditorElementDefinition[] {
    const definitions: LandingEditorElementDefinition[] = [{ id: 'page.root', label: 'Page Root', kind: 'container' }];

    editorSectionPaletteKeys.forEach(section => {
      definitions.push({
        id: `section.${section}`,
        label: `${landingEditorSectionLabels[section]} Container`,
        kind: 'section',
        section,
        parentId: 'page.root',
      });
    });

    const add = (definition: LandingEditorElementDefinition): void => { definitions.push(definition); };
    const addContainer = (id: string, label: string, section: LandingHomeSectionKey, parentId = `section.${section}`, repeat?: { path: string; index: number }) =>
      add({
        id,
        label,
        section,
        parentId,
        kind: 'container',
        ...(repeat ? { repeatPath: repeat.path, repeatIndex: repeat.index } : {}),
      });
    const addText = (id: string, label: string, path: string, section: LandingHomeSectionKey, multiline = false, parentId?: string) => add({ id, label, path, section, multiline, parentId: parentId ?? `section.${section}`, kind: 'text', typographyMode: typographyModeForText(path) });
    const addUrl = (id: string, label: string, path: string, section: LandingHomeSectionKey, parentId?: string) => add({ id, label, path, section, parentId: parentId ?? `section.${section}`, kind: 'url' });
    const addImage = (id: string, label: string, section: LandingHomeSectionKey, path?: string, parentId?: string, maskHostId?: string) => add({ id, label, path, section, parentId: parentId ?? `section.${section}`, kind: 'image', ...(maskHostId ? { maskHostId } : {}) });
    const addVideo = (id: string, label: string, section: LandingHomeSectionKey, path?: string, parentId?: string) => add({ id, label, path, section, parentId: parentId ?? `section.${section}`, kind: 'video' });
    const addIcon = (id: string, label: string, path: string, section: LandingHomeSectionKey, parentId?: string) => add({ id, label, path, section, parentId: parentId ?? `section.${section}`, kind: 'icon' });

    addContainer('navigation.container', 'Navigation Container', 'hero');
    addImage('navigation.logo', 'Navigation: Logo', 'hero', 'brandLogo', 'navigation.container');
    addText('navigation.eyebrow', 'Navigation: Infobar', 'hero.eyebrow', 'hero', false, 'navigation.container');
    content.nav.forEach((_, index) => {
      addContainer(`navigation.item.${index}`, `Navigation ${index + 1} Container`, 'hero', 'navigation.container', {
        path: 'nav',
        index,
      });
      addText(`navigation.item.${index}.label`, `Navigation ${index + 1}: Label`, `nav.${index}.label`, 'hero', false, `navigation.item.${index}`);
      addUrl(`navigation.item.${index}.href`, `Navigation ${index + 1}: Link`, `nav.${index}.href`, 'hero', `navigation.item.${index}`);
    });
    addText('navigation.primaryAction.label', 'Navigation: Bewerbungs-CTA', 'hero.primaryAction.label', 'hero', false, 'navigation.container');
    definitions.push(...industryNavigationEditorDefinitions(content));
    definitions.push(...mobileNavigationEditorDefinitions(content));
    addText('navigation.companyPrimaryAction.label', 'Navigation: Unternehmens-CTA', 'consultationCompany.primaryAction.label', 'consultationCompany', false, 'navigation.container');

    addVideo('hero.video', 'Hero: Hintergrundvideo', 'hero', 'hero.videoUrl');
    addContainer('hero.copy', 'Hero Copy', 'hero');
    addContainer('hero.actions', 'Hero Buttons', 'hero', 'hero.copy');
    addText('hero.title', 'Hero: Titel', 'hero.title', 'hero', false, 'hero.copy');
    addText('hero.highlight', 'Hero: Hervorhebung', 'hero.highlight', 'hero', false, 'hero.copy');
    addText('hero.intro', 'Hero: Intro', 'hero.intro', 'hero', true, 'hero.copy');
    if (content.hero?.bullets) {
      content.hero.bullets.forEach((_, index) => addText(`hero.bullet.${index}`, `Hero: Bullet ${index + 1}`, `hero.bullets.${index}`, 'hero', true, 'hero.copy'));
    }
    addText('hero.primaryAction.label', 'Hero: Primärer Button', 'hero.primaryAction.label', 'hero', false, 'hero.actions');
    addUrl('hero.primaryAction.href', 'Hero: Primärer Button-Link', 'hero.primaryAction.href', 'hero', 'hero.actions');
    addText('hero.secondaryAction.label', 'Hero: Sekundärer Button', 'hero.secondaryAction.label', 'hero', false, 'hero.actions');
    addUrl('hero.secondaryAction.href', 'Hero: Sekundärer Button-Link', 'hero.secondaryAction.href', 'hero', 'hero.actions');

    const addAudienceHero = (section: 'heroApplicant' | 'heroCompany' | 'heroFanApplicant' | 'heroFanCompany', label: string) => {
      const block = content[section];
      if (!block) return; // Fail-safe

      addContainer(`${section}.copy`, `${label}: Kopf`, section);
      addContainer(`${section}.cards`, `${label}: Karten`, section);
      addText(`${section}.badge`, `${label}: Badge`, `${section}.badge`, section, false, `${section}.copy`);
      addText(`${section}.title`, `${label}: Titel`, `${section}.title`, section, false, `${section}.copy`);
      addText(`${section}.highlight`, `${label}: Hervorhebung`, `${section}.highlight`, section, false, `${section}.copy`);
      addText(`${section}.intro`, `${label}: Intro`, `${section}.intro`, section, true, `${section}.copy`);
      addText(`${section}.trustLabel`, `${label}: Trust-Hinweis`, `${section}.trustLabel`, section, false, `${section}.copy`);
      block.cards?.forEach((_, index) => {
        addContainer(`${section}.card.${index}`, `${label}: Karte ${index + 1}`, section, `${section}.cards`, {
          path: `${section}.cards`,
          index,
        });
        addImage(`${section}.card.${index}.image`, `${label}: Karte ${index + 1} Bild`, section, `${section}.cards.${index}.image`, `${section}.card.${index}`);
        addText(`${section}.card.${index}.eyebrow`, `${label}: Karte ${index + 1} Label`, `${section}.cards.${index}.eyebrow`, section, false, `${section}.card.${index}`);
        addText(`${section}.card.${index}.title`, `${label}: Karte ${index + 1} Titel`, `${section}.cards.${index}.title`, section, false, `${section}.card.${index}`);
        addUrl(`${section}.card.${index}.href`, `${label}: Karte ${index + 1} Link`, `${section}.cards.${index}.href`, section, `${section}.card.${index}`);
        // The fan variants reveal a CTA — expose its copy so it stays editable in WYSIWYG.
        if (section === 'heroFanApplicant' || section === 'heroFanCompany') {
          addText(`${section}.card.${index}.quoteAuthor`, `${label}: Karte ${index + 1} CTA-Titel`, `${section}.cards.${index}.quoteAuthor`, section, false, `${section}.card.${index}`);
          addText(`${section}.card.${index}.quote`, `${label}: Karte ${index + 1} CTA-Text`, `${section}.cards.${index}.quote`, section, true, `${section}.card.${index}`);
          addText(`${section}.card.${index}.quoteRole`, `${label}: Karte ${index + 1} CTA-Button`, `${section}.cards.${index}.quoteRole`, section, false, `${section}.card.${index}`);
        }
      });
    };
    addAudienceHero('heroApplicant', 'Hero Bewerber'); addAudienceHero('heroCompany', 'Hero Unternehmen');
    addAudienceHero('heroFanApplicant', 'Hero Fan Bewerber');
    addAudienceHero('heroFanCompany', 'Hero Fan Unternehmen');
    definitions.push(...companyJourneyEditorDefinitions(content), ...companyPeopleHeroEditorDefinitions(content));
    const addGlobusCampaignHero = (
      section: 'globusCampaignApplicant' | 'globusCampaignCompany',
      label: string,
    ) => {
      const block = content[section];
      if (!block) return;
      addContainer(`${section}.copy`, `${label}: Text`, section);
      addContainer(`${section}.media`, `${label}: Bildstapel`, section);
      addContainer(`${section}.cards`, `${label}: Arbeitsbereiche`, section);
      addText(`${section}.title`, `${label}: Titel`, `${section}.title`, section, true, `${section}.copy`);
      addText(`${section}.intro`, `${label}: Intro`, `${section}.intro`, section, true, `${section}.copy`);
      block.mediaCards.forEach((_, index) => {
        addContainer(`${section}.mediaCard.${index}`, `${label}: Bildkarte ${index + 1}`, section, `${section}.media`, {
          path: `${section}.mediaCards`,
          index,
        });
        addImage(`${section}.mediaCard.${index}.image`, `${label}: Bildkarte ${index + 1} Bild`, section, `${section}.mediaCards.${index}.image`, `${section}.mediaCard.${index}`);
        addText(`${section}.mediaCard.${index}.imageAlt`, `${label}: Bildkarte ${index + 1} Alternativtext`, `${section}.mediaCards.${index}.imageAlt`, section, true, `${section}.mediaCard.${index}`);
      });
      addText(`${section}.primaryAction.label`, `${label}: Primärer Button`, `${section}.primaryAction.label`, section, false, `${section}.copy`);
      addUrl(`${section}.primaryAction.href`, `${label}: Primärer Button-Link`, `${section}.primaryAction.href`, section, `${section}.copy`);
      addText(`${section}.secondaryAction.label`, `${label}: Sekundärer Button`, `${section}.secondaryAction.label`, section, false, `${section}.copy`);
      addUrl(`${section}.secondaryAction.href`, `${label}: Sekundärer Button-Link`, `${section}.secondaryAction.href`, section, `${section}.copy`);
      block.cards.forEach((_, index) => {
        addContainer(`${section}.card.${index}`, `${label}: Bereich ${index + 1}`, section, `${section}.cards`, {
          path: `${section}.cards`,
          index,
        });
        addImage(`${section}.card.${index}.image`, `${label}: Bereich ${index + 1} Bild`, section, `${section}.cards.${index}.image`, `${section}.card.${index}`);
        addText(`${section}.card.${index}.title`, `${label}: Bereich ${index + 1} Titel`, `${section}.cards.${index}.title`, section, false, `${section}.card.${index}`);
        addUrl(`${section}.card.${index}.href`, `${label}: Bereich ${index + 1} Link`, `${section}.cards.${index}.href`, section, `${section}.card.${index}`);
      });
    }; addGlobusCampaignHero('globusCampaignApplicant', 'GLOBUS Kampagne Bewerber');
    addGlobusCampaignHero('globusCampaignCompany', 'GLOBUS Kampagne Unternehmen');
    addContainer('intro.heading', 'Willkommen Heading', 'intro'); addContainer('intro.metrics.container', 'Kennzahlen Container', 'intro');
    addText('intro.eyebrow', 'Willkommen: Eyebrow', 'intro.eyebrow', 'intro', false, 'intro.heading');
    addText('intro.title', 'Willkommen: Titel', 'intro.title', 'intro', true, 'intro.heading');
    addText('intro.titleHighlight', 'Willkommen: Highlight-Wort', 'intro.titleHighlight', 'intro', false, 'intro.heading');
    addText('intro.text', 'Willkommen: Text', 'intro.text', 'intro', true, 'intro.heading');
    content.intro.metrics.forEach((_, index) => {
      addContainer(`intro.metric.${index}`, `Kennzahl ${index + 1} Container`, 'intro', 'intro.metrics.container', {
        path: 'intro.metrics',
        index,
      });
      addText(`intro.metric.${index}.value`, `Kennzahl ${index + 1}: Wert`, `intro.metrics.${index}.value`, 'intro', false, `intro.metric.${index}`);
      addText(`intro.metric.${index}.label`, `Kennzahl ${index + 1}: Text`, `intro.metrics.${index}.label`, 'intro', true, `intro.metric.${index}`);
    });

    addContainer('industryOverview.heading', 'Branchen Heading', 'industryOverview');
    addContainer('industryOverview.items', 'Branchen Cards Container', 'industryOverview');
    addText('industryOverview.eyebrow', 'Branchen: Eyebrow', 'industryOverview.eyebrow', 'industryOverview', false, 'industryOverview.heading');
    addText('industryOverview.title', 'Branchen: Titel', 'industryOverview.title', 'industryOverview', true, 'industryOverview.heading');
    addText('industryOverview.titleHighlight', 'Branchen: Highlight-Wort', 'industryOverview.titleHighlight', 'industryOverview', false, 'industryOverview.heading');
    addText('industryOverview.text', 'Branchen: Text', 'industryOverview.text', 'industryOverview', true, 'industryOverview.heading');
    (content.industryOverview?.items ?? []).forEach((_, index) => {
      addContainer(`industryOverview.item.${index}`, `Branche ${index + 1} Container`, 'industryOverview', 'industryOverview.items', { path: 'industryOverview.items', index });
      addIcon(`industryOverview.item.${index}.icon`, `Branche ${index + 1}: Icon`, `industryOverview.items.${index}.iconClass`, 'industryOverview', `industryOverview.item.${index}`);
      addText(`industryOverview.item.${index}.title`, `Branche ${index + 1}: Titel`, `industryOverview.items.${index}.title`, 'industryOverview', false, `industryOverview.item.${index}`);
      addText(`industryOverview.item.${index}.text`, `Branche ${index + 1}: Text`, `industryOverview.items.${index}.text`, 'industryOverview', true, `industryOverview.item.${index}`);
      addText(`industryOverview.item.${index}.linkLabel`, `Branche ${index + 1}: Link-Text`, `industryOverview.items.${index}.linkLabel`, 'industryOverview', false, `industryOverview.item.${index}`);
      addUrl(`industryOverview.item.${index}.href`, `Branche ${index + 1}: Link`, `industryOverview.items.${index}.href`, 'industryOverview', `industryOverview.item.${index}`);
    });

    addContainer('laborShortage.heading', 'Fachkräftemangel Heading', 'laborShortage');
    addContainer('laborShortage.stats', 'Fachkräftemangel Kennzahlen', 'laborShortage');
    addContainer('laborShortage.points', 'Fachkräftemangel Punkte', 'laborShortage');
    addContainer('laborShortage.sources', 'Fachkräftemangel Quellen', 'laborShortage');
    addText('laborShortage.eyebrow', 'Fachkräftemangel: Eyebrow', 'laborShortage.eyebrow', 'laborShortage', false, 'laborShortage.heading');
    addText('laborShortage.title', 'Fachkräftemangel: Titel', 'laborShortage.title', 'laborShortage', true, 'laborShortage.heading');
    addText('laborShortage.titleHighlight', 'Fachkräftemangel: Highlight-Wort', 'laborShortage.titleHighlight', 'laborShortage', false, 'laborShortage.heading');
    addText('laborShortage.text', 'Fachkräftemangel: Text', 'laborShortage.text', 'laborShortage', true, 'laborShortage.heading');
    (content.laborShortage?.stats ?? []).forEach((_, index) => {
      addContainer(`laborShortage.stat.${index}`, `Kennzahl ${index + 1} Container`, 'laborShortage', 'laborShortage.stats', { path: 'laborShortage.stats', index });
      addText(`laborShortage.stat.${index}.value`, `Kennzahl ${index + 1}: Wert`, `laborShortage.stats.${index}.value`, 'laborShortage', false, `laborShortage.stat.${index}`);
      addText(`laborShortage.stat.${index}.label`, `Kennzahl ${index + 1}: Text`, `laborShortage.stats.${index}.label`, 'laborShortage', true, `laborShortage.stat.${index}`);
      addText(`laborShortage.stat.${index}.sourceLabel`, `Kennzahl ${index + 1}: Quelle`, `laborShortage.stats.${index}.sourceLabel`, 'laborShortage', false, `laborShortage.stat.${index}`);
      addUrl(`laborShortage.stat.${index}.sourceHref`, `Kennzahl ${index + 1}: Quellen-Link`, `laborShortage.stats.${index}.sourceHref`, 'laborShortage', `laborShortage.stat.${index}`);
    });
    (content.laborShortage?.points ?? []).forEach((_, index) => {
      addContainer(`laborShortage.point.${index}`, `Punkt ${index + 1} Container`, 'laborShortage', 'laborShortage.points', { path: 'laborShortage.points', index });
      addIcon(`laborShortage.point.${index}.icon`, `Punkt ${index + 1}: Icon`, `laborShortage.points.${index}.iconClass`, 'laborShortage', `laborShortage.point.${index}`);
      addText(`laborShortage.point.${index}.title`, `Punkt ${index + 1}: Titel`, `laborShortage.points.${index}.title`, 'laborShortage', false, `laborShortage.point.${index}`);
      addText(`laborShortage.point.${index}.text`, `Punkt ${index + 1}: Text`, `laborShortage.points.${index}.text`, 'laborShortage', true, `laborShortage.point.${index}`);
    });
    (content.laborShortage?.sources ?? []).forEach((_, index) => {
      addContainer(`laborShortage.source.${index}`, `Quelle ${index + 1} Container`, 'laborShortage', 'laborShortage.sources', { path: 'laborShortage.sources', index });
      addText(`laborShortage.source.${index}.label`, `Quelle ${index + 1}: Label`, `laborShortage.sources.${index}.label`, 'laborShortage', false, `laborShortage.source.${index}`);
      addUrl(`laborShortage.source.${index}.href`, `Quelle ${index + 1}: Link`, `laborShortage.sources.${index}.href`, 'laborShortage', `laborShortage.source.${index}`);
    });

    addContainer('partners.heading', 'Partner Heading', 'partners');
    addContainer('partners.items', 'Partner Matrix Container', 'partners');
    addText('partners.eyebrow', 'Partner: Eyebrow', 'partners.eyebrow', 'partners', false, 'partners.heading');
    addText('partners.title', 'Partner: Titel', 'partners.title', 'partners', true, 'partners.heading');
    addText('partners.titleHighlight', 'Partner: Highlight-Wort', 'partners.titleHighlight', 'partners', false, 'partners.heading');
    addText('partners.text', 'Partner: Text', 'partners.text', 'partners', true, 'partners.heading');
    addText('partners.trustLabel', 'Partner: Trust-Hinweis', 'partners.trustLabel', 'partners', false, 'partners.heading');
    (content.partners?.items ?? []).forEach((_, index) => {
      addContainer(`partners.item.${index}`, `Partner ${index + 1} Container`, 'partners', 'partners.items', {
        path: 'partners.items',
        index,
      });
      addImage(`partners.item.${index}.logo`, `Partner ${index + 1}: Logo`, 'partners', `partners.items.${index}.logo`, `partners.item.${index}`);
      addText(`partners.item.${index}.name`, `Partner ${index + 1}: Name`, `partners.items.${index}.name`, 'partners', false, `partners.item.${index}`);
      addText(`partners.item.${index}.description`, `Partner ${index + 1}: Beschreibung`, `partners.items.${index}.description`, 'partners', true, `partners.item.${index}`);
      addUrl(`partners.item.${index}.href`, `Partner ${index + 1}: Link`, `partners.items.${index}.href`, 'partners', `partners.item.${index}`);
    });

    addContainer('globusPartner.layout', 'Globus Auto Layout', 'globusPartner');
    addContainer('globusPartner.logoPanel', 'Globus Logo Container', 'globusPartner', 'globusPartner.layout');
    addContainer('globusPartner.copy', 'Globus Text Container', 'globusPartner', 'globusPartner.layout');
    addText('globusPartner.eyebrow', 'Globus: Label', 'globusPartner.eyebrow', 'globusPartner', false, 'globusPartner.copy');
    addText('globusPartner.title', 'Globus: Titel', 'globusPartner.title', 'globusPartner', true, 'globusPartner.copy');
    addText('globusPartner.text', 'Globus: Text', 'globusPartner.text', 'globusPartner', true, 'globusPartner.copy');
    addImage('globusPartner.image', 'Globus: Gruppenfoto', 'globusPartner', 'globusPartner.image', 'globusPartner.logoPanel', 'globusPartner.logoPanel');
    addText('globusPartner.imageAlt', 'Globus: Gruppenfoto Alternativtext', 'globusPartner.imageAlt', 'globusPartner', true, 'globusPartner.logoPanel');
    addImage('globusPartner.logo', 'Globus: Logo', 'globusPartner', 'globusPartner.logo', 'globusPartner.logoPanel');
    addText('globusPartner.primaryAction.label', 'Globus: Button-Text', 'globusPartner.primaryAction.label', 'globusPartner', false, 'globusPartner.copy');
    addUrl('globusPartner.primaryAction.href', 'Globus: Button-Link', 'globusPartner.primaryAction.href', 'globusPartner', 'globusPartner.copy');

    addContainer('reasons.heading', 'Gründe Heading', 'reasons');
    addContainer('reasons.imageStack', 'Bild Container', 'reasons');
    addContainer('reasons.cards.container', 'Gründe Cards Container', 'reasons');
    addText('reasons.title', 'Gründe: Titel', 'reasons.title', 'reasons', true, 'reasons.heading');
    addText('reasons.titleHighlight', 'Gründe: Highlight-Wort', 'reasons.titleHighlight', 'reasons', false, 'reasons.heading');
    addText('reasons.text', 'Gründe: Text', 'reasons.text', 'reasons', true, 'reasons.heading');
    addImage('reasons.articleImage', 'Gründe: Bild', 'reasons', 'reasons.image', 'reasons.imageStack');
    content.reasons.cards.forEach((_, index) => {
      addContainer(`reasons.card.${index}`, `Grund ${index + 1} Container`, 'reasons', 'reasons.cards.container', {
        path: 'reasons.cards',
        index,
      });
      addIcon(`reasons.card.${index}.icon`, `Grund ${index + 1}: Icon`, `reasons.cards.${index}.iconClass`, 'reasons', `reasons.card.${index}`);
      addText(`reasons.card.${index}.title`, `Grund ${index + 1}: Titel`, `reasons.cards.${index}.title`, 'reasons', false, `reasons.card.${index}`);
      addText(`reasons.card.${index}.text`, `Grund ${index + 1}: Text`, `reasons.cards.${index}.text`, 'reasons', true, `reasons.card.${index}`);
    });
    addText('reasons.primaryAction.label', 'Gründe: CTA', 'hero.primaryAction.label', 'reasons', false, 'reasons.heading');

    addContainer('benefits.heading', 'Vorteile Heading', 'benefits');
    addContainer('benefits.items.container', 'Vorteile Container', 'benefits');
    addText('benefits.title', 'Vorteile: Titel', 'benefits.title', 'benefits', true, 'benefits.heading');
    addText('benefits.titleHighlight', 'Vorteile: Highlight-Wort', 'benefits.titleHighlight', 'benefits', false, 'benefits.heading');
    content.benefits.items.forEach((_, index) => {
      addContainer(`benefits.item.${index}`, `Vorteil ${index + 1} Container`, 'benefits', 'benefits.items.container', {
        path: 'benefits.items',
        index,
      });
      addIcon(`benefits.item.${index}.icon`, `Vorteil ${index + 1}: Icon`, `benefits.items.${index}.iconClass`, 'benefits', `benefits.item.${index}`);
      addText(`benefits.item.${index}.title`, `Vorteil ${index + 1}: Titel`, `benefits.items.${index}.title`, 'benefits', false, `benefits.item.${index}`);
      addText(`benefits.item.${index}.text`, `Vorteil ${index + 1}: Text`, `benefits.items.${index}.text`, 'benefits', true, `benefits.item.${index}`);
    });

    addContainer('costs.heading', 'Kosten Heading', 'costs');
    addContainer('costs.panel', 'Kosten Panel', 'costs');
    addContainer('costs.included.container', 'Koordination Container', 'costs', 'costs.panel');
    addContainer('costs.excluded.container', 'Hinweise Container', 'costs', 'costs.panel');
    addText('costs.title', 'Kosten: Titel', 'costs.title', 'costs', true, 'costs.heading');
    addText('costs.titleHighlight', 'Kosten: Highlight-Wort', 'costs.titleHighlight', 'costs', false, 'costs.heading');
    addText('costs.text', 'Kosten: Text', 'costs.text', 'costs', true, 'costs.heading');
    addText('costs.includedTitle', 'Kosten: Linke Spalte', 'costs.includedTitle', 'costs', false, 'costs.included.container');
    addText('costs.excludedTitle', 'Kosten: Rechte Spalte', 'costs.excludedTitle', 'costs', false, 'costs.excluded.container');
    addText('costs.primaryAction.label', 'Kosten: CTA', 'hero.primaryAction.label', 'costs', false, 'costs.heading');
    content.costs.included.forEach((_, index) => { addText(`costs.included.${index}`, `Kosten: Koordination ${index + 1}`, `costs.included.${index}`, 'costs', true, 'costs.included.container'); });
    content.costs.excluded.forEach((_, index) => { addText(`costs.excluded.${index}`, `Kosten: Hinweis ${index + 1}`, `costs.excluded.${index}`, 'costs', true, 'costs.excluded.container'); });

    addContainer('recognition.heading', 'Anerkennung Heading', 'recognition');
    addContainer('recognition.cards.container', 'Anerkennung Cards Container', 'recognition');
    addText('recognition.title', 'Anerkennung: Titel', 'recognition.title', 'recognition', true, 'recognition.heading');
    addText('recognition.titleHighlight', 'Anerkennung: Highlight-Wort', 'recognition.titleHighlight', 'recognition', false, 'recognition.heading');
    addText('recognition.text', 'Anerkennung: Text', 'recognition.text', 'recognition', true, 'recognition.heading');
    content.recognition.cards.forEach((_, index) => {
      addContainer(`recognition.card.${index}`, `Anerkennung ${index + 1} Container`, 'recognition', 'recognition.cards.container', { path: 'recognition.cards', index });
      addText(`recognition.card.${index}.title`, `Anerkennung ${index + 1}: Titel`, `recognition.cards.${index}.title`, 'recognition', false, `recognition.card.${index}`);
      addIcon(`recognition.card.${index}.icon`, `Anerkennung ${index + 1}: Icon`, `recognition.cards.${index}.iconClass`, 'recognition', `recognition.card.${index}`);
      addText(`recognition.card.${index}.text`, `Anerkennung ${index + 1}: Text`, `recognition.cards.${index}.text`, 'recognition', true, `recognition.card.${index}`);
    });

    addContainer('process.heading', 'Ablauf Heading', 'process');
    addContainer('process.steps.container', 'Ablauf Schritte Container', 'process');
    addText('process.eyebrow', 'Ablauf: Eyebrow', 'process.eyebrow', 'process', false, 'process.heading');
    addText('process.title', 'Ablauf: Titel', 'process.title', 'process', true, 'process.heading');
    addText('process.titleHighlight', 'Ablauf: Highlight-Wort', 'process.titleHighlight', 'process', false, 'process.heading');
    addText('process.text', 'Ablauf: Text', 'process.text', 'process', true, 'process.heading');
    content.process.steps.forEach((_, index) => {
      addContainer(`process.step.${index}`, `Schritt ${index + 1} Container`, 'process', 'process.steps.container', {
        path: 'process.steps',
        index,
      });
      addIcon(`process.step.${index}.icon`, `Schritt ${index + 1}: Icon`, `process.steps.${index}.iconClass`, 'process', `process.step.${index}`);
      addImage(`process.step.${index}.image`, `Schritt ${index + 1}: Bild`, 'process', `process.steps.${index}.image`, `process.step.${index}`);
      addText(`process.step.${index}.imageAlt`, `Schritt ${index + 1}: Alternativtext`, `process.steps.${index}.imageAlt`, 'process', true, `process.step.${index}`);
      addText(`process.step.${index}.title`, `Schritt ${index + 1}: Titel`, `process.steps.${index}.title`, 'process', false, `process.step.${index}`);
      addText(`process.step.${index}.text`, `Schritt ${index + 1}: Text`, `process.steps.${index}.text`, 'process', true, `process.step.${index}`);
    });
    addContainer('team.heading', 'Team Heading', 'team');
    addContainer('team.grid', 'Team Grid Container', 'team');
    addText('team.eyebrow', 'Team: Eyebrow', 'team.eyebrow', 'team', false, 'team.heading');
    addText('team.title', 'Team: Titel', 'team.title', 'team', true, 'team.heading');
    addText('team.titleHighlight', 'Team: Highlight-Wort', 'team.titleHighlight', 'team', false, 'team.heading');
    addText('team.text', 'Team: Text', 'team.text', 'team', true, 'team.heading');
    content.team.members.forEach((_, index) => {
      addContainer(`team.member.${index}`, `Team ${index + 1} Container`, 'team', 'team.grid', {
        path: 'team.members',
        index,
      });
      addText(`team.member.${index}.name`, `Team ${index + 1}: Name`, `team.members.${index}.name`, 'team', false, `team.member.${index}`);
      addText(`team.member.${index}.role`, `Team ${index + 1}: Rolle`, `team.members.${index}.role`, 'team', false, `team.member.${index}`);
      addText(`team.member.${index}.text`, `Team ${index + 1}: Text`, `team.members.${index}.text`, 'team', true, `team.member.${index}`);
      addText(`team.member.${index}.story`, `Team ${index + 1}: Story`, `team.members.${index}.story`, 'team', true, `team.member.${index}`);
      addText(`team.member.${index}.motivation`, `Team ${index + 1}: Motivation`, `team.members.${index}.motivation`, 'team', true, `team.member.${index}`);
      addImage(`team.member.${index}.image`, `Team ${index + 1}: Bild`, 'team', `team.members.${index}.image`, `team.member.${index}`);
      addUrl(`team.member.${index}.linkedinUrl`, `Team ${index + 1}: LinkedIn-Profil`, `team.members.${index}.linkedinUrl`, 'team', `team.member.${index}`);
    });

    addContainer('story.testimonials', 'Testimonials Container', 'story');
    content.story.testimonials?.forEach((_, index) => {
      addContainer(`story.testimonial.${index}`, `Testimonial ${index + 1} Container`, 'story', 'story.testimonials', {
        path: 'story.testimonials',
        index,
      });
      addText(`story.testimonial.${index}.quote`, `Testimonial ${index + 1}: Zitat`, `story.testimonials.${index}.quote`, 'story', true, `story.testimonial.${index}`);
      addText(`story.testimonial.${index}.author`, `Testimonial ${index + 1}: Name`, `story.testimonials.${index}.author`, 'story', false, `story.testimonial.${index}`);
      addText(`story.testimonial.${index}.role`, `Testimonial ${index + 1}: Rolle`, `story.testimonials.${index}.role`, 'story', false, `story.testimonial.${index}`);
      addImage(`story.testimonial.${index}.image`, `Testimonial ${index + 1}: Bild`, 'story', `story.testimonials.${index}.image`, `story.testimonial.${index}`);
      addImage(`story.testimonial.${index}.logo`, `Testimonial ${index + 1}: Logo`, 'story', `story.testimonials.${index}.logo`, `story.testimonial.${index}`);
    });

    addContainer('info.heading', 'Info Heading', 'info');
    addContainer('info.links.container', 'Info Links Container', 'info');
    addText('info.title', 'Info: Titel', 'info.title', 'info', true, 'info.heading');
    addText('info.titleHighlight', 'Info: Highlight-Wort', 'info.titleHighlight', 'info', false, 'info.heading');
    content.info.links.forEach((_, index) => {
      addContainer(`info.link.${index}`, `Info-Link ${index + 1} Container`, 'info', 'info.links.container', {
        path: 'info.links',
        index,
      });
      addText(`info.link.${index}.label`, `Info-Link ${index + 1}: Label`, `info.links.${index}.label`, 'info', false, `info.link.${index}`);
      addUrl(`info.link.${index}.href`, `Info-Link ${index + 1}: URL`, `info.links.${index}.href`, 'info', `info.link.${index}`);
    });

    addContainer('contact.copy', 'Kontakt Copy Container', 'contact');
    addContainer('contact.actions', 'Kontakt Buttons', 'contact', 'contact.copy');
    addContainer('contact.card', 'Kontakt Card Container', 'contact');
    addText('contact.title', 'Kontakt: Titel', 'contact.title', 'contact', true, 'contact.copy');
    addText('contact.titleHighlight', 'Kontakt: Highlight-Wort', 'contact.titleHighlight', 'contact', false, 'contact.copy');
    addText('contact.text', 'Kontakt: Text', 'contact.text', 'contact', true, 'contact.copy');
    addText('contact.primaryAction.label', 'Kontakt: Primärer Button', 'contact.primaryAction.label', 'contact', false, 'contact.actions');
    addUrl('contact.primaryAction.href', 'Kontakt: Primärer Button-Link', 'contact.primaryAction.href', 'contact', 'contact.actions');
    addText('contact.secondaryAction.label', 'Kontakt: Sekundärer Button', 'contact.secondaryAction.label', 'contact', false, 'contact.actions');
    addUrl('contact.secondaryAction.href', 'Kontakt: Sekundärer Button-Link', 'contact.secondaryAction.href', 'contact', 'contact.actions');
    addText('contact.address', 'Kontakt: Adresse', 'contact.address', 'contact', true, 'contact.card');
    addText('contact.email', 'Kontakt: E-Mail', 'contact.email', 'contact', false, 'contact.card');
    addText('contact.hours', 'Kontakt: Zeiten', 'contact.hours', 'contact', false, 'contact.card');

    addContainer('requirements.heading', 'Voraussetzungen Heading', 'requirements');
    addContainer('requirements.columns', 'Voraussetzungen Spalten Container', 'requirements');
    addText('requirements.title', 'Voraussetzungen: Titel', 'requirements.title', 'requirements', true, 'requirements.heading');
    addText('requirements.titleHighlight', 'Voraussetzungen: Highlight-Wort', 'requirements.titleHighlight', 'requirements', false, 'requirements.heading');
    addText('requirements.text', 'Voraussetzungen: Text', 'requirements.text', 'requirements', true, 'requirements.heading');
    (content.requirements?.columns ?? []).forEach((column, index) => {
      addContainer(`requirements.column.${index}`, `Spalte ${index + 1} Container`, 'requirements', 'requirements.columns', { path: 'requirements.columns', index });
      addIcon(`requirements.column.${index}.icon`, `Spalte ${index + 1}: Icon`, `requirements.columns.${index}.iconClass`, 'requirements', `requirements.column.${index}`);
      addText(`requirements.column.${index}.title`, `Spalte ${index + 1}: Titel`, `requirements.columns.${index}.title`, 'requirements', false, `requirements.column.${index}`);
      (column.items ?? []).forEach((_, itemIndex) => {
        addText(`requirements.column.${index}.item.${itemIndex}`, `Spalte ${index + 1}: Punkt ${itemIndex + 1}`, `requirements.columns.${index}.items.${itemIndex}`, 'requirements', true, `requirements.column.${index}`);
      });
    });

    const addFaqSection = (section: 'faqApplicant' | 'faqCompany', label: string) => {
      addContainer(`${section}.heading`, `${label} Heading`, section);
      addContainer(`${section}.items`, `${label} Fragen Container`, section);
      addText(`${section}.title`, `${label}: Titel`, `${section}.title`, section, true, `${section}.heading`);
      addText(`${section}.titleHighlight`, `${label}: Highlight-Wort`, `${section}.titleHighlight`, section, false, `${section}.heading`);
      addText(`${section}.text`, `${label}: Text`, `${section}.text`, section, true, `${section}.heading`);
      (content[section]?.items ?? []).forEach((_, index) => {
        addContainer(`${section}.item.${index}`, `Frage ${index + 1} Container`, section, `${section}.items`, {
          path: `${section}.items`,
          index,
        });
        addText(`${section}.item.${index}.question`, `Frage ${index + 1}: Frage`, `${section}.items.${index}.question`, section, true, `${section}.item.${index}`);
        addText(`${section}.item.${index}.answer`, `Frage ${index + 1}: Antwort`, `${section}.items.${index}.answer`, section, true, `${section}.item.${index}`);
      });
    };
    addFaqSection('faqApplicant', 'FAQ Pflegekräfte');
    addFaqSection('faqCompany', 'FAQ Arbeitgeber');

    addContainer('quality.heading', 'Qualität Heading', 'quality');
    addContainer('quality.stats', 'Qualität Kennzahlen Container', 'quality');
    addContainer('quality.points', 'Qualität Punkte Container', 'quality');
    addText('quality.eyebrow', 'Qualität: Eyebrow', 'quality.eyebrow', 'quality', false, 'quality.heading');
    addText('quality.title', 'Qualität: Titel', 'quality.title', 'quality', true, 'quality.heading');
    addText('quality.titleHighlight', 'Qualität: Highlight-Wort', 'quality.titleHighlight', 'quality', false, 'quality.heading');
    addText('quality.text', 'Qualität: Text', 'quality.text', 'quality', true, 'quality.heading');
    (content.quality?.stats ?? []).forEach((_, index) => {
      addContainer(`quality.stat.${index}`, `Kennzahl ${index + 1} Container`, 'quality', 'quality.stats', {
        path: 'quality.stats',
        index,
      });
      addText(`quality.stat.${index}.value`, `Kennzahl ${index + 1}: Wert`, `quality.stats.${index}.value`, 'quality', false, `quality.stat.${index}`);
      addText(`quality.stat.${index}.label`, `Kennzahl ${index + 1}: Text`, `quality.stats.${index}.label`, 'quality', true, `quality.stat.${index}`);
    });
    (content.quality?.points ?? []).forEach((_, index) => {
      addContainer(`quality.point.${index}`, `Punkt ${index + 1} Container`, 'quality', 'quality.points', {
        path: 'quality.points',
        index,
      });
      addIcon(`quality.point.${index}.icon`, `Punkt ${index + 1}: Icon`, `quality.points.${index}.iconClass`, 'quality', `quality.point.${index}`);
      addText(`quality.point.${index}.title`, `Punkt ${index + 1}: Titel`, `quality.points.${index}.title`, 'quality', false, `quality.point.${index}`);
      addText(`quality.point.${index}.text`, `Punkt ${index + 1}: Text`, `quality.points.${index}.text`, 'quality', true, `quality.point.${index}`);
    });

    addContainer('knowledge.heading', 'Wissen Heading', 'knowledge');
    addContainer('knowledge.cards', 'Wissen Cards Container', 'knowledge');
    addText('knowledge.title', 'Wissen: Titel', 'knowledge.title', 'knowledge', true, 'knowledge.heading');
    addText('knowledge.titleHighlight', 'Wissen: Highlight-Wort', 'knowledge.titleHighlight', 'knowledge', false, 'knowledge.heading');
    addText('knowledge.text', 'Wissen: Text', 'knowledge.text', 'knowledge', true, 'knowledge.heading');
    (content.knowledge?.articles ?? []).forEach((_, index) => {
      addContainer(`knowledge.card.${index}`, `Artikel ${index + 1} Container`, 'knowledge', 'knowledge.cards', {
        path: 'knowledge.articles',
        index,
      });
      addIcon(`knowledge.card.${index}.icon`, `Artikel ${index + 1}: Icon`, `knowledge.articles.${index}.iconClass`, 'knowledge', `knowledge.card.${index}`);
      addText(`knowledge.card.${index}.eyebrow`, `Artikel ${index + 1}: Label`, `knowledge.articles.${index}.eyebrow`, 'knowledge', false, `knowledge.card.${index}`);
      addText(`knowledge.card.${index}.title`, `Artikel ${index + 1}: Titel`, `knowledge.articles.${index}.title`, 'knowledge', false, `knowledge.card.${index}`);
      addText(`knowledge.card.${index}.text`, `Artikel ${index + 1}: Text`, `knowledge.articles.${index}.text`, 'knowledge', true, `knowledge.card.${index}`);
      addText(`knowledge.card.${index}.linkLabel`, `Artikel ${index + 1}: Link-Text`, `knowledge.articles.${index}.linkLabel`, 'knowledge', false, `knowledge.card.${index}`);
      addUrl(`knowledge.card.${index}.link`, `Artikel ${index + 1}: Link-Ziel`, `knowledge.articles.${index}.href`, 'knowledge', `knowledge.card.${index}`);
    });

    addContainer('values.heading', 'Werte Heading', 'values');
    addContainer('values.items', 'Werte Container', 'values');
    addContainer('values.bridge', 'Werte Brücke Container', 'values');
    addText('values.eyebrow', 'Werte: Eyebrow', 'values.eyebrow', 'values', false, 'values.heading');
    addText('values.title', 'Werte: Titel', 'values.title', 'values', true, 'values.heading');
    addText('values.titleHighlight', 'Werte: Highlight-Wort', 'values.titleHighlight', 'values', false, 'values.heading');
    addText('values.text', 'Werte: Text', 'values.text', 'values', true, 'values.heading');
    (content.values?.items ?? []).forEach((_, index) => {
      addContainer(`values.item.${index}`, `Wert ${index + 1} Container`, 'values', 'values.items', {
        path: 'values.items',
        index,
      });
      addIcon(`values.item.${index}.icon`, `Wert ${index + 1}: Icon`, `values.items.${index}.iconClass`, 'values', `values.item.${index}`);
      addText(`values.item.${index}.title`, `Wert ${index + 1}: Titel`, `values.items.${index}.title`, 'values', false, `values.item.${index}`);
      addText(`values.item.${index}.text`, `Wert ${index + 1}: Text`, `values.items.${index}.text`, 'values', true, `values.item.${index}`);
    });
    addText('values.bridgeTitle', 'Brücke: Titel', 'values.bridgeTitle', 'values', false, 'values.bridge');
    addText('values.bridgeText', 'Brücke: Text', 'values.bridgeText', 'values', true, 'values.bridge');
    addText('values.bridgeAction.label', 'Brücke: Button-Text', 'values.bridgeAction.label', 'values', false, 'values.bridge');
    addUrl('values.bridgeAction.href', 'Brücke: Button-Link', 'values.bridgeAction.href', 'values', 'values.bridge');

    add({ id: 'footer.container', label: 'Footer Container', kind: 'container', parentId: 'page.root' });
    addImage('footer.logo', 'Footer: Logo', 'contact', 'brandLogo', 'footer.container');
    addText('footer.text', 'Footer: Text', 'footer.text', 'contact', true, 'footer.container');
    content.footer.links.forEach((_, index) => {
      addContainer(`footer.link.${index}`, `Footer-Link ${index + 1}: ${content.footer.links[index]?.label ?? ''}`, 'contact', 'footer.container', {
        path: 'footer.links',
        index,
      });
      addText(`footer.link.${index}.label`, `Footer-Link ${index + 1}: Label`, `footer.links.${index}.label`, 'contact', false, `footer.link.${index}`);
      addUrl(`footer.link.${index}.href`, `Footer-Link ${index + 1}: URL`, `footer.links.${index}.href`, 'contact', `footer.link.${index}`);
    });
    content.footer.socialLinks?.forEach((_, index) => {
      addContainer(`footer.social.${index}`, `Footer Social ${index + 1} Container`, 'contact', 'footer.container', {
        path: 'footer.socialLinks',
        index,
      });
      addText(`footer.social.${index}.label`, `Footer Social ${index + 1}: Label`, `footer.socialLinks.${index}.label`, 'contact', false, `footer.social.${index}`);
      addUrl(`footer.social.${index}.href`, `Footer Social ${index + 1}: URL`, `footer.socialLinks.${index}.href`, 'contact', `footer.social.${index}`);
    });

    return appendMissingContentDefinitions(definitions, content);
  }

  static staticPageDefinitions(pageKey: StaticPageKey, page: StaticPageContent, content?: LandingContent, sections: LandingHomeSectionKey[] = []): LandingEditorElementDefinition[] {
    if (pageKey === 'bewerben' && content) { page = { ...applicationPagePresentation(page, content), faq: undefined }; sections = [...new Set([...sections, 'faqApplicant' as const])]; }
    const definitions: LandingEditorElementDefinition[] = [{ id: 'page.root', label: `${this.staticPageLabel(pageKey)} Root`, kind: 'container' }];
    const add = (definition: LandingEditorElementDefinition) => definitions.push({ source: 'static', staticPageKey: pageKey, ...definition });
    const addContainer = (id: string, label: string, parentId = 'page.root', repeat?: { path: string; index: number }) =>
      add({
        id,
        label,
        parentId,
        kind: 'container',
        ...(repeat ? { repeatPath: repeat.path, repeatIndex: repeat.index } : {}),
      });
    const addText = (id: string, label: string, path: string, parentId: string, multiline = false) => add({ id, label, path, parentId, multiline, kind: 'text', typographyMode: typographyModeForText(path) });
    const addUrl = (id: string, label: string, path: string, parentId: string) => add({ id, label, path, parentId, kind: 'url' });
    const addImage = (id: string, label: string, path: string, parentId: string) => add({ id, label, path, parentId, kind: 'image' });
    const addVideo = (id: string, label: string, path: string, parentId: string) => add({ id, label, path, parentId, kind: 'video' });
    const addIcon = (id: string, label: string, path: string, parentId: string) => add({ id, label, path, parentId, kind: 'icon' });

    const prefix = `static.${pageKey}`;
    const heroId = page.key === 'team' ? `${prefix}.teamHero` : `${prefix}.hero`;
    const usesStaticHeroLayout = isIndustryPageKey(page.key) || page.key === 'bewerben' || page.key === 'unternehmen' || page.key === 'ablauf' || page.key === 'ueber-vivaporta' || page.key === 'impressum';
    addContainer(heroId, page.key === 'team' ? 'Team Hero' : 'Hero', 'page.root');
    if (page.key !== 'team') {
      const heroLayoutId = `${heroId}.layout`, heroCopyId = `${heroId}.copy`;
      if (usesStaticHeroLayout) { addContainer(heroLayoutId, 'Hero Layout', heroId); addContainer(heroCopyId, 'Hero Text', heroLayoutId); }
      const copyParentId = usesStaticHeroLayout ? heroCopyId : heroId;
      addText(`${heroId}.eyebrow`, page.key === 'impressum' ? 'Hero: Seitentitel' : 'Hero: Eyebrow', 'eyebrow', copyParentId);
      addText(`${heroId}.title`, page.key === 'impressum' ? 'Pflichtangaben: Überschrift' : 'Hero: Titel', 'title', page.key === 'impressum' ? `${prefix}.cards` : copyParentId, true);
      if (page.heroTitleHighlight !== undefined) addText(`${heroId}.titleHighlight`, 'Hero: blau markiertes Wort', 'heroTitleHighlight', `${heroId}.title`, false);
      addText(`${heroId}.intro`, 'Hero: Intro', 'intro', copyParentId, true);
    }
    if (page.heroStageTitle !== undefined) addText(`${heroId}.heroStageTitle`, 'Hero: Poster-Titel', 'heroStageTitle', heroId, true);
    if (page.heroImage !== undefined) addImage(`${heroId}.heroImage`, 'Hero: Bild', 'heroImage', usesStaticHeroLayout ? `${heroId}.layout` : heroId);
    definitions.push(...industryGalleryEditorDefinitions(pageKey, page, heroId));
    if (page.heroVideo !== undefined && !isIndustryPageKey(page.key)) add({ id: `${heroId}.heroVideo`, label: 'Hero: Video', path: 'heroVideo', parentId: heroId, kind: 'video' });
    (page.heroHighlights ?? []).forEach((_, index) => {
      addText(`${heroId}.heroHighlight.${index}`, `Hero: Chip ${index + 1}`, `heroHighlights.${index}`, heroId, false);
    });
    if (page.heroAction) {
      addText(`${heroId}.heroAction.label`, 'Hero: CTA-Text', 'heroAction.label', heroId, false);
      addUrl(`${heroId}.heroAction.href`, 'Hero: CTA-Link', 'heroAction.href', heroId);
    }

    if (page.missionStory) {
      const missionStoryId = `${prefix}.missionStory`;
      addContainer(missionStoryId, 'Mission: Geschichte', 'page.root');
      addText(`${missionStoryId}.eyebrow`, 'Mission: Eyebrow', 'missionStory.eyebrow', missionStoryId, false);
      addText(`${missionStoryId}.title`, 'Mission: Titel', 'missionStory.title', missionStoryId, true);
      addText(`${missionStoryId}.slogan`, 'Mission: Slogan', 'missionStory.slogan', missionStoryId, false);
      addText(`${missionStoryId}.intro`, 'Mission: Intro', 'missionStory.intro', missionStoryId, true);
      page.missionStory.blocks.forEach((block, blockIndex) => {
        const blockId = `${missionStoryId}.block.${blockIndex}`;
        addContainer(blockId, `Mission: Abschnitt ${blockIndex + 1}`, missionStoryId, {
          path: 'missionStory.blocks',
          index: blockIndex,
        });
        addText(`${blockId}.title`, `Mission: Abschnitt ${blockIndex + 1} Titel`, `missionStory.blocks.${blockIndex}.title`, blockId, false);
        block.paragraphs.forEach((_, paragraphIndex) => {
          addText(`${blockId}.paragraph.${paragraphIndex}`, `Mission: Abschnitt ${blockIndex + 1} Text ${paragraphIndex + 1}`, `missionStory.blocks.${blockIndex}.paragraphs.${paragraphIndex}.text`, blockId, true);
        });
      });
      addContainer(`${missionStoryId}.closing`, 'Mission: Abschluss', missionStoryId);
      addText(`${missionStoryId}.closing.title`, 'Mission: Abschluss Titel', 'missionStory.closing.title', `${missionStoryId}.closing`, true);
      page.missionStory.closing.paragraphs.forEach((_, paragraphIndex) => {
        addText(`${missionStoryId}.closing.paragraph.${paragraphIndex}`, `Mission: Abschluss Text ${paragraphIndex + 1}`, `missionStory.closing.paragraphs.${paragraphIndex}.text`, `${missionStoryId}.closing`, true);
      });
    }

    // Keep each hero portrait individually selectable and replaceable on the editor canvas.
    if (page.key === 'team' && page.team?.length) {
      addText(`${heroId}.title`, 'Team Hero: Titel', 'title', heroId, true);
      page.team.slice(0, 6).forEach((_, index) => {
        const portraitId = `${heroId}.portrait.${index}`;
        addContainer(portraitId, `Hero-Portrait ${index + 1}`, heroId);
        addImage(`${portraitId}.image`, `Hero-Portrait ${index + 1}: Bild`, `team.${index}.image`, portraitId);
      });
    }

    if (page.cards.length) {
      addContainer(`${prefix}.cards`, page.key === 'kontakt' ? 'Kontakt-Karten' : 'Karten', 'page.root');
      if (page.cardsEyebrow !== undefined) addText(`${prefix}.cards.eyebrow`, 'Karten: Überschriftzeile', 'cardsEyebrow', `${prefix}.cards`);
      if (page.cardsTitle !== undefined) addText(`${prefix}.cards.title`, 'Karten: Titel', 'cardsTitle', `${prefix}.cards`, true);
      const cardsParentId = page.key === 'ablauf' ? `${prefix}.cards.container` : `${prefix}.cards`;
      if (page.key === 'ablauf') addContainer(cardsParentId, 'Ablauf Schritte Container', `${prefix}.cards`);
      page.cards.forEach((card, index) => {
        const cardId = `${prefix}.card.${index}`;
        addContainer(cardId, `Karte ${index + 1}`, cardsParentId, { path: 'cards', index });
        addIcon(`${cardId}.icon`, `Karte ${index + 1}: Icon`, `cards.${index}.iconClass`, cardId);
        if (page.key === 'ablauf' || card.image !== undefined) {
          addImage(`${cardId}.image`, `Karte ${index + 1}: Bild`, `cards.${index}.image`, cardId);
          addText(`${cardId}.imageAlt`, `Karte ${index + 1}: Alternativtext`, `cards.${index}.imageAlt`, cardId, true);
        }
        addText(`${cardId}.title`, `Karte ${index + 1}: Titel`, `cards.${index}.title`, cardId, false);
        addText(`${cardId}.text`, `Karte ${index + 1}: Text`, `cards.${index}.text`, cardId, true);
      });
    }

    if (page.sections?.length) {
      page.sections.forEach((section, sectionIndex) => {
        const sectionId = `${prefix}.section.${sectionIndex}`;
        addContainer(sectionId, `Inhaltsblock ${sectionIndex + 1}`, 'page.root', {
          path: 'sections',
          index: sectionIndex,
        });
        addContainer(`${sectionId}.heading`, `Inhaltsblock ${sectionIndex + 1}: Heading`, sectionId);
        addText(`${sectionId}.eyebrow`, `Inhaltsblock ${sectionIndex + 1}: Eyebrow`, `sections.${sectionIndex}.eyebrow`, `${sectionId}.heading`);
        addText(`${sectionId}.title`, `Inhaltsblock ${sectionIndex + 1}: Titel`, `sections.${sectionIndex}.title`, `${sectionId}.heading`, true);
        if (section.titleHighlight !== undefined) {
          addText(`${sectionId}.titleHighlight`, `Inhaltsblock ${sectionIndex + 1}: Highlight-Wort`, `sections.${sectionIndex}.titleHighlight`, `${sectionId}.heading`);
        }
        addText(`${sectionId}.text`, `Inhaltsblock ${sectionIndex + 1}: Text`, `sections.${sectionIndex}.text`, `${sectionId}.heading`, true);
        if (section.image !== undefined) {
          addImage(`${sectionId}.image`, `Inhaltsblock ${sectionIndex + 1}: Bild`, `sections.${sectionIndex}.image`, sectionId);
        }
        (section.cards ?? []).forEach((_, cardIndex) => {
          const cardId = `${sectionId}.card.${cardIndex}`;
          addContainer(cardId, `Inhaltsblock ${sectionIndex + 1}: Karte ${cardIndex + 1}`, sectionId, {
            path: `sections.${sectionIndex}.cards`,
            index: cardIndex,
          });
          addIcon(`${cardId}.icon`, `Karte ${cardIndex + 1}: Icon`, `sections.${sectionIndex}.cards.${cardIndex}.iconClass`, cardId);
          addText(`${cardId}.title`, `Karte ${cardIndex + 1}: Titel`, `sections.${sectionIndex}.cards.${cardIndex}.title`, cardId);
          addText(`${cardId}.text`, `Karte ${cardIndex + 1}: Text`, `sections.${sectionIndex}.cards.${cardIndex}.text`, cardId, true);
        });
      });
    }

    registerGlobusJobEditorDefinitions(page, prefix, {
      add,
      addContainer,
      addText,
      addUrl,
      addImage,
      addVideo,
    });

    if (page.faq?.length) {
      addContainer(`${prefix}.faq`, 'FAQ', 'page.root');
      page.faq.forEach((_, index) => {
        const itemId = `${prefix}.faq.${index}`;
        addContainer(itemId, `FAQ ${index + 1}`, `${prefix}.faq`, { path: 'faq', index });
        addText(`${itemId}.question`, `FAQ ${index + 1}: Frage`, `faq.${index}.question`, itemId, true);
        addText(`${itemId}.answer`, `FAQ ${index + 1}: Antwort`, `faq.${index}.answer`, itemId, true);
      });
    }

    if (page.cta) {
      addContainer(`${prefix}.cta`, 'CTA', 'page.root');
      addContainer(`${prefix}.cta.heading`, 'CTA Heading', `${prefix}.cta`);
      addText(`${prefix}.cta.eyebrow`, 'CTA: Eyebrow', 'cta.eyebrow', `${prefix}.cta.heading`);
      addText(`${prefix}.cta.title`, 'CTA: Titel', 'cta.title', `${prefix}.cta.heading`, true);
      addText(`${prefix}.cta.text`, 'CTA: Text', 'cta.text', `${prefix}.cta.heading`, true);
      if (page.cta.image) addImage(`${prefix}.cta.image`, 'CTA: Bild', 'cta.image', `${prefix}.cta`);
      addText(`${prefix}.cta.primaryAction.label`, 'CTA: Primärer Button', 'cta.primaryAction.label', `${prefix}.cta`);
      addUrl(`${prefix}.cta.primaryAction.href`, 'CTA: Primärer Button-Link', 'cta.primaryAction.href', `${prefix}.cta`);
      addText(`${prefix}.cta.secondaryAction.label`, 'CTA: Sekundärer Button', 'cta.secondaryAction.label', `${prefix}.cta`);
      addUrl(`${prefix}.cta.secondaryAction.href`, 'CTA: Sekundärer Button-Link', 'cta.secondaryAction.href', `${prefix}.cta`);
    }

    if (page.team?.length) {
      addContainer(`${prefix}.team`, 'Team', 'page.root');
      page.team.forEach((_, index) => {
        const memberId = `${prefix}.team.member.${index}`;
        addContainer(memberId, `Team ${index + 1}`, `${prefix}.team`, { path: 'team', index });
        addImage(`${memberId}.image`, `Team ${index + 1}: Bild`, `team.${index}.image`, memberId);
        addText(`${memberId}.name`, `Team ${index + 1}: Name`, `team.${index}.name`, memberId, false);
        addText(`${memberId}.role`, `Team ${index + 1}: Rolle`, `team.${index}.role`, memberId, false);
        addText(`${memberId}.text`, `Team ${index + 1}: Text`, `team.${index}.text`, memberId, true);
        addText(`${memberId}.story`, `Team ${index + 1}: Story`, `team.${index}.story`, memberId, true);
        addText(`${memberId}.motivation`, `Team ${index + 1}: Motivation`, `team.${index}.motivation`, memberId, true);
        addUrl(`${memberId}.linkedinUrl`, `Team ${index + 1}: LinkedIn-Profil`, `team.${index}.linkedinUrl`, memberId);
      });
    }

    if (page.contact) {
      addContainer(`${prefix}.contact`, 'Kontakt', 'page.root');
      addText(`${prefix}.contactEyebrow`, 'Kontakt: Eyebrow', 'contact.eyebrow', `${prefix}.contact`, false);
      addText(`${prefix}.contactTitle`, 'Kontakt: Titel', 'contact.title', `${prefix}.contact`, true);
      addText(`${prefix}.contactText`, 'Kontakt: Text', 'contact.text', `${prefix}.contact`, true);
      addText(`${prefix}.contactEmail`, 'Kontakt: E-Mail', 'contact.email', `${prefix}.contact`, false);
      addText(`${prefix}.contactPhone`, 'Kontakt: Telefon', 'contact.phone', `${prefix}.contact`, false);
      addText(`${prefix}.contactAddress`, 'Kontakt: Adresse', 'contact.address', `${prefix}.contact`, true);
      addUrl(`${prefix}.map`, 'Kontakt: Karten-URL', 'contact.mapEmbedUrl', `${prefix}.contact`);
      for (const [field, label] of [
        ['title', 'Karte: Überschrift'], ['text', 'Karte: Datenschutzhinweis'],
        ['loadLabel', 'Karte: Freigabe-Button'], ['unloadLabel', 'Karte: Widerruf-Button'],
        ['privacyLabel', 'Karte: Datenschutz-Link'], ['loadingLabel', 'Karte: Ladehinweis'], ['activeLabel', 'Karte: Freigabe-Status'],
      ] as const) addText(`${prefix}.map.${field}`, label, `contact.mapConsent.${field}`, `${prefix}.map`, field === 'text');
    }

    // Modular landing sections placed on this static page. They reuse the landing definitions verbatim
    // (same element ids and landing content source) so editing stays consistent across every page.
    if (content && sections.length) {
      const landingDefinitions = this.definitions(content);
      for (const section of sections) {
        landingDefinitions.filter(definition => definition.section === section && !definition.id.startsWith('navigation.') && !definition.id.startsWith('footer.')).forEach(definition => { definitions.push(definition); });
      }
    }

    if (content) {
      definitions.push(...this.definitions(content).filter(definition => definition.id.startsWith('footer.')));
      definitions.push(...industryStaticNavigationEditorDefinitions(content));
    }

    const complete = appendMissingContentDefinitions(definitions, page, {
      source: 'static',
      staticPageKey: pageKey,
    });
    return isIndustryPageKey(pageKey) ? complete.filter(definition => definition.path !== 'heroVideo') : complete;
  }

  static getById(content: LandingContent, id: string) {
    return this.definitions(content).find(definition => definition.id === id);
  }

  private static staticPageLabel(pageKey: StaticPageKey) {
    return editorPages.find(page => page.key === pageKey)?.label ?? pageKey;
  }
}

const isArrayIndex = (segment: string) => /^\d+$/.test(segment);

export const getValueAtPath = (source: unknown, path: string): string => {
  let current = source;
  for (const segment of path.split('.')) {
    if (current === undefined || current === null) return '';
    current = (current as Record<string, unknown>)[segment];
  }
  return typeof current === 'string' ? current : '';
};

export const setValueAtPath = (source: Record<string, unknown>, path: string, value: string) => {
  const segments = path.split('.');
  let current: Record<string, unknown> | unknown[] = source;

  segments.forEach((segment, index) => {
    const isLast = index === segments.length - 1;
    if (isLast) {
      if (Array.isArray(current) && isArrayIndex(segment)) current[Number(segment)] = value;
      else (current as Record<string, unknown>)[segment] = value;
      return;
    }

    const nextSegment = segments[index + 1]!;
    const currentRecord = current as Record<string, unknown>;
    const next = Array.isArray(current) && isArrayIndex(segment) ? current[Number(segment)] : currentRecord[segment];
    if (typeof next !== 'object' || next === null) {
      const replacement = isArrayIndex(nextSegment) ? [] : {};
      if (Array.isArray(current) && isArrayIndex(segment)) current[Number(segment)] = replacement;
      else currentRecord[segment] = replacement;
    }

    current = (Array.isArray(current) && isArrayIndex(segment) ? current[Number(segment)] : currentRecord[segment]) as Record<string, unknown> | unknown[];
  });
};
